import { useContext, useState } from 'react';
import { Alert, Box, Button, Chip, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import SettingsContentCard from './SettingsContentCard';
import IOSSwitch from '../Switch/IOSSwitch';
import CurrencyContext from '../../context/CurrencyContext';
import { saveEnabledCurrencies, saveManualRate } from '../../services/ExchangeRateService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { CURRENCIES, DEFAULT_CURRENCY, formatMoney, formatNumber, parseMoney } from '../../utils/currency';

const SOURCE_LABEL = { BCU: 'BCU (oficial)', MANUAL: 'Manual' };
const formatDate = (iso) => (iso ? String(iso).split('-').reverse().join('/') : '');

/** Monedas habilitadas del tenant y su cotización contra UYU (BCU automática, con carga manual de respaldo). */
const switchRowSx = { mb: 1.5, ml: 0, mr: 0, gap: 1.5, '& .MuiFormControlLabel-label': { ml: 0 } };

export default function CurrencySettingsTab({ readOnly = false }) {
  const { enabledCurrencies, rateViews, refresh, catalog } = useContext(CurrencyContext);
  const [saving, setSaving] = useState(false);
  const [manualRates, setManualRates] = useState({});
  // Lista de monedas soportadas: la informa el backend (enum Currency); CURRENCIES es el respaldo mientras carga.
  const supported = catalog && catalog.length ? catalog.map((item) => CURRENCIES[item.code] || item) : Object.values(CURRENCIES);
  const extraCurrencies = supported.filter((c) => c.code !== DEFAULT_CURRENCY);

  const toggle = async (code) => {
    if (readOnly || saving) return;
    const next = enabledCurrencies.includes(code) ? enabledCurrencies.filter((c) => c !== code) : [...enabledCurrencies, code];
    setSaving(true);
    try {
      await saveEnabledCurrencies(next);
      await refresh();
    } catch (error) {
      console.error('Error saving currencies', error);
      SnackbarUtils.error('No se pudieron guardar las monedas');
    } finally {
      setSaving(false);
    }
  };

  const saveManual = async (code) => {
    const rate = parseMoney(manualRates[code]);
    if (!(rate > 0)) {
      SnackbarUtils.warning('Ingresá una cotización válida');
      return;
    }
    setSaving(true);
    try {
      await saveManualRate({ currency: code, rate });
      setManualRates((prev) => ({ ...prev, [code]: '' }));
      await refresh();
      SnackbarUtils.success('Cotización guardada');
    } catch (error) {
      console.error('Error saving manual rate', error);
      SnackbarUtils.error('No se pudo guardar la cotización');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <SettingsContentCard
        title="Monedas"
        description={`Los pesos (${CURRENCIES[DEFAULT_CURRENCY].symbol}) siempre están habilitados. Habilitá otra moneda para poder cargar precios, costos y compras en ella; los tickets de venta usan una sola moneda.`}
      >
        <Stack sx={{ mt: 2 }}>
          <FormControlLabel control={<IOSSwitch checked disabled />} label={`${CURRENCIES[DEFAULT_CURRENCY].label} (${DEFAULT_CURRENCY})`} sx={switchRowSx} />
          {extraCurrencies.map((currency) => (
            <FormControlLabel
              key={currency.code}
              control={<IOSSwitch checked={enabledCurrencies.includes(currency.code)} onChange={() => toggle(currency.code)} disabled={readOnly || saving} />}
              label={`${currency.label} (${currency.code})`}
              sx={switchRowSx}
            />
          ))}
        </Stack>
      </SettingsContentCard>

      {extraCurrencies.filter((c) => enabledCurrencies.includes(c.code)).map((currency) => {
        const view = rateViews.find((r) => r.currency === currency.code);
        return (
          <SettingsContentCard
            key={currency.code}
            title={`Cotización ${currency.code}`}
            description={currency.bcuPublished === false ? 'El BCU no publica esta cotización: cargala a mano.' : 'Se actualiza sola con la cotización oficial del BCU. Si el BCU no responde se sigue usando la última conocida.'}
          >
            {view ? (
              <Box>
                <Typography variant="h5" fontWeight={500}>
                  {`1 ${currency.code} = ${formatMoney(view.rate, DEFAULT_CURRENCY, { decimals: 3 })}`}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip size="small" label={`Del ${formatDate(view.rateDate)}`} />
                  <Chip size="small" label={SOURCE_LABEL[view.source] || view.source} color={view.source === 'MANUAL' ? 'warning' : 'default'} />
                </Stack>
                {view.stale && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {`La última cotización tiene ${view.ageDays} días. Se sigue usando para vender, pero conviene revisar que el BCU esté respondiendo o cargar una manual.`}
                  </Alert>
                )}
              </Box>
            ) : (
              <Alert severity="warning">Todavía no hay una cotización cargada. Cargá una manual para poder vender en {currency.code}.</Alert>
            )}
            {!readOnly && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }} alignItems={{ sm: 'flex-start' }}>
                <TextField
                  size="small"
                  label={`Cotización manual (${formatNumber(1, 0)} ${currency.code} en pesos)`}
                  value={manualRates[currency.code] || ''}
                  onChange={(event) => setManualRates((prev) => ({ ...prev, [currency.code]: event.target.value }))}
                  helperText="Reemplaza a la del BCU de hoy. Usá coma para los decimales."
                />
                <Button variant="outlined" onClick={() => saveManual(currency.code)} disabled={saving}>Guardar cotización</Button>
              </Stack>
            )}
          </SettingsContentCard>
        );
      })}
    </Stack>
  );
}
